import i18n from '../i18n';

/**
 * How long ago something happened, from a number of seconds the **server** measured.
 *
 * `Intl.RelativeTimeFormat` and no translation key: the browser already knows « il y a 3 heures »,
 * "3 hours ago" and «hace 3 horas», with the right plural in each — three keys and three plural
 * rules of our own would be three chances to get one wrong. Same locale mapping as `formatDate`,
 * read at call time so the wording follows a language change.
 *
 * The seconds are never computed here from a timestamp: `elapsedSeconds` comes out of Postgres
 * (`NOW() - sent_at`), which keeps one clock in the system instead of two that drift.
 */

const INTL_LOCALES: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES' };

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Minutes under an hour, hours under a day, days beyond — the granularity someone chasing a
 * pending request actually reads. Seconds are rounded **up** to a minute rather than shown:
 * « il y a 4 secondes » on a request just sent is noise.
 *
 * `numeric: 'always'` and not `'auto'`: the friendly form says « hier » and « avant-hier », which
 * drop the figure the reader is actually comparing. « il y a 2 jours » beside « il y a 3 jours »
 * is a wait one can rank at a glance; « avant-hier » beside « il y a 3 jours » is not.
 */
export const formatElapsed = (seconds: number): string => {
  const locale = INTL_LOCALES[i18n.language] ?? INTL_LOCALES.fr;
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  const elapsed = Math.max(0, seconds);

  if (elapsed < HOUR) return format.format(-Math.max(1, Math.round(elapsed / MINUTE)), 'minute');
  if (elapsed < DAY) return format.format(-Math.round(elapsed / HOUR), 'hour');
  return format.format(-Math.round(elapsed / DAY), 'day');
};

/**
 * The same seconds read as a **duration** rather than as a moment — « 12 minutes », "3 hours".
 * `formatElapsed` answers "how long ago", this one answers "how long it took", which is what a
 * response delay is. Same buckets and same locale map, so the two can never disagree.
 */
export const formatDuration = (seconds: number): string => {
  const locale = INTL_LOCALES[i18n.language] ?? INTL_LOCALES.fr;
  const format = (amount: number, unit: string): string =>
    // `short` and not `long`: « en 15 min » is what a dense column and a phone both need, and
    // `Intl` abbreviates per locale — « 15 min », "15 hr", « 15 h » — so no wording of ours grows
    // a third form to keep in step.
    new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' }).format(amount);
  const elapsed = Math.max(0, seconds);

  if (elapsed < HOUR) return format(Math.max(1, Math.round(elapsed / MINUTE)), 'minute');
  if (elapsed < DAY) return format(Math.round(elapsed / HOUR), 'hour');
  return format(Math.round(elapsed / DAY), 'day');
};
